// src/services/tables/draftColumns.js - Draft Picks Table Columns
import React from 'react';
import { Plus, Info } from 'lucide-react';
import { dynastyTheme } from '../colorService';
import { renderDefault, renderFloat1, renderFloat2, renderFloat3, renderAvg } from './DynastyTable';
import { STAT_FIELD_MAP, DEFAULT_BATTING_STATS, DEFAULT_PITCHING_STATS } from '../../utils/statMapping';
import { STATIC_HITTING_FIELDS, STATIC_PITCHING_FIELDS, statFieldSimple } from '../../utils/statColumnDedup';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';

/**
 * Create columns for Draft Picks table
 * Shows all picks with player stats, price, position, etc.
 */
export const createDraftPicksColumns = ({
  onPlayerClick,
  isYourTurn = false,
  userTeamId = null
}) => {
  return [
    // Pick Number - matches BoxScore POS column style
    {
      key: 'overall_pick_number',
      title: '#',
      width: 45,
      sortable: true,
      render: (pick) => (
        <div className={`text-sm font-semibold ${dynastyTheme.classes.text.warning}`}>
          {pick.overall_pick_number || pick.overall_pick || '?'}
        </div>
      )
    },

    // Round - matches BoxScore stat column style
    {
      key: 'round',
      title: 'Rd',
      width: 45,
      sortable: true,
      render: (pick) => (
        <div className={dynastyTheme.classes.text.white}>
          {pick.round || pick.round_number || '?'}
        </div>
      )
    },

    // Team - not bold, smaller font
    {
      key: 'team_name',
      title: 'Team',
      width: 140,
      sortable: true,
      render: (pick) => {
        const teamName = pick.current_owner?.team_name || pick.team_name || 'TBD';
        const isUserTeam = pick.current_owner?.team_id === userTeamId || pick.team_id === userTeamId;

        return (
          <div className={`text-left text-xs ${isUserTeam ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight}`}>
            {teamName}
          </div>
        );
      }
    },

    // Player Name - matches BoxScore Player column style
    {
      key: 'player_name',
      title: 'Player',
      width: 160,
      sortable: true,
      allowOverflow: true,
      align: 'left',
      render: (pick) => {
        // Handle both WebSocket format (league_player_id) and API format (selection.player_id)
        const hasPlayer = pick.league_player_id || pick.selection?.player_id;

        if (!hasPlayer) {
          return (
            <div className={`text-left text-sm ${dynastyTheme.classes.text.neutralDark} italic`}>
              {pick.current_owner?.team_id === userTeamId && isYourTurn ? 'Your Pick' : 'Pending...'}
            </div>
          );
        }

        // Get player name from canonical structure, with WebSocket fallback
        const playerName = pick.player?.info?.full_name ||
                          `${pick.player?.info?.first_name || ''} ${pick.player?.info?.last_name || ''}`.trim() ||
                          'Unknown';

        return (
          <div className="text-left flex items-center gap-1">
            <PlayerLink
              mlbId={pick.player?.ids?.mlb}
              name={playerName}
              style={{ color: '#f9fafb' }}
            />
            <InjuryBadge mlbId={pick.player?.ids?.mlb} />
          </div>
        );
      }
    },

    // Position - matches BoxScore POS column style
    // LF/CF/RF display as OF (league uses generic OF slots)
    {
      key: 'position',
      title: 'POS',
      width: 50,
      sortable: true,
      render: (pick) => {
        const rawPos = pick.player?.info?.position || '-';
        const position = ['LF', 'CF', 'RF'].includes(rawPos) ? 'OF' : rawPos;
        return (
          <div className={`text-sm font-semibold ${dynastyTheme.classes.text.warning}`}>
            {position}
          </div>
        );
      }
    },

    // MLB Team - matches BoxScore Team column style (gray)
    {
      key: 'mlb_team',
      title: 'MLB',
      width: 55,
      sortable: true,
      render: (pick) => {
        const mlbTeam = pick.player?.info?.mlb_team || '-';
        return (
          <div className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight}`}>
            {mlbTeam}
          </div>
        );
      }
    },

    // Market Price - highlighted like BoxScore HR column
    {
      key: 'baseline_price',
      title: 'Price',
      width: 70,
      sortable: true,
      render: (pick) => {
        // Handle both WebSocket format (league_player_id) and API format (selection.player_id)
        const hasPlayer = pick.league_player_id || pick.selection?.player_id;

        if (!hasPlayer) {
          return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
        }

        // Market price comes from player_prices table, or salary from selection
        const baselinePrice = pick.player?.financial?.baseline_price ||
                           pick.selection?.salary ||
                           0;
        return (
          <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
            ${Math.round(baselinePrice)}
          </div>
        );
      }
    },
    
    // Season Stats - R (matches BoxScore stat column style)
    {
      key: 'season_runs',
      title: 'R',
      width: 50,
      sortable: true,
      render: (pick, rowType) => {
        const hasPlayer = pick.league_player_id || pick.selection?.player_id;
        if (!hasPlayer) return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
        const stats = pick.player?.stats?.season?.hitting || {};
        const value = stats.runs || stats.R || 0;
        return (
          <div className={dynastyTheme.classes.text.white}>
            {renderDefault(value)}
          </div>
        );
      }
    },

    // Season Stats - HR (highlighted like BoxScore)
    {
      key: 'season_hr',
      title: 'HR',
      width: 50,
      sortable: true,
      render: (pick, rowType) => {
        const hasPlayer = pick.league_player_id || pick.selection?.player_id;
        if (!hasPlayer) return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
        const stats = pick.player?.stats?.season?.hitting || {};
        const value = stats.home_runs || stats.HR || 0;
        return (
          <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
            {renderDefault(value)}
          </div>
        );
      }
    },

    // Season Stats - RBI (matches BoxScore stat column style)
    {
      key: 'season_rbi',
      title: 'RBI',
      width: 50,
      sortable: true,
      render: (pick, rowType) => {
        const hasPlayer = pick.league_player_id || pick.selection?.player_id;
        if (!hasPlayer) return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
        const stats = pick.player?.stats?.season?.hitting || {};
        const value = stats.rbi || stats.RBI || 0;
        return (
          <div className={dynastyTheme.classes.text.white}>
            {renderDefault(value)}
          </div>
        );
      }
    },

    // Season Stats - SB (matches BoxScore stat column style)
    {
      key: 'season_sb',
      title: 'SB',
      width: 50,
      sortable: true,
      render: (pick, rowType) => {
        const hasPlayer = pick.league_player_id || pick.selection?.player_id;
        if (!hasPlayer) return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
        const stats = pick.player?.stats?.season?.hitting || {};
        const value = stats.stolen_bases || stats.SB || 0;
        return (
          <div className={dynastyTheme.classes.text.white}>
            {renderDefault(value)}
          </div>
        );
      }
    },

    // Season Stats - AVG (matches BoxScore stat column style)
    {
      key: 'season_avg',
      title: 'AVG',
      width: 60,
      sortable: true,
      render: (pick, rowType) => {
        const hasPlayer = pick.league_player_id || pick.selection?.player_id;
        if (!hasPlayer) return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
        const stats = pick.player?.stats?.season?.hitting || {};
        const value = stats.batting_avg || stats.AVG || 0;
        return (
          <div className={dynastyTheme.classes.text.white}>
            {renderAvg(value)}
          </div>
        );
      }
    },

    // Season Stats - OPS (matches BoxScore stat column style)
    {
      key: 'season_ops',
      title: 'OPS',
      width: 60,
      sortable: true,
      render: (pick, rowType) => {
        const hasPlayer = pick.league_player_id || pick.selection?.player_id;
        if (!hasPlayer) return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
        const stats = pick.player?.stats?.season?.hitting || {};
        const value = stats.ops || stats.OPS || 0;
        return (
          <div className={dynastyTheme.classes.text.white}>
            {renderFloat3(value)}
          </div>
        );
      }
    },

    // Time Picked (matches BoxScore secondary text style)
    {
      key: 'picked_at',
      title: 'Time',
      width: 80,
      sortable: true,
      render: (pick) => {
        const pickedAt = pick.picked_at || pick.selection?.picked_at;
        if (!pickedAt) return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
        const date = new Date(pickedAt);
        return (
          <div className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight}`}>
            {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        );
      }
    }
  ];
};

/**
 * Create columns for Available Players table in live draft
 * Shows player info, market price, and stats based on selected year
 *
 * Stat columns are dynamically generated from league scoring_categories
 *
 * @param {Object} params
 * @param {Function} params.onPlayerSelect - Callback when player is clicked to select for draft
 * @param {Function} params.onViewProfile - Callback when info icon is clicked to view player profile
 * @param {Function} params.onAddToQueue - Callback when player is added to queue
 * @param {string} params.statsKey - Stats object key: 'season' for current, 'season_YYYY' for previous
 * @param {string} params.activeTab - 'hitting' or 'pitching'
 * @param {Object} params.scoringCategories - { hitting: [...], pitching: [...] } from league settings
 */
export const createDraftAvailableColumns = ({
  onPlayerSelect,
  onViewProfile,
  onAddToQueue,
  statsKey = 'season',
  activeTab = 'hitting',
  scoringCategories = { hitting: null, pitching: null }
}) => {
  const isHitter = activeTab === 'hitting';

  const baseColumns = [
    // Ranking - shows position in current sort order (1, 2, 3...)
    {
      key: 'impact_rank',
      title: 'Rank',
      width: 55,
      sortable: true,
      sortValue: (player) => player?.financial?.z_score || 0,
      render: (player) => {
        // _displayRank is added by VirtualAvailablePlayersTable after sorting
        const rank = player?._displayRank;
        return (
          <div className={dynastyTheme.classes.text.white}>
            {rank || '-'}
          </div>
        );
      }
    },

    // Position - matches BoxScore POS column style
    // LF/CF/RF display as OF (league uses generic OF slots)
    {
      key: 'position',
      title: 'POS',
      width: 50,
      sortable: true,
      sortValue: (player) => {
        const pos = player?.info?.position || '';
        return ['LF', 'CF', 'RF'].includes(pos) ? 'OF' : pos;
      },
      render: (player) => {
        const rawPos = player?.info?.position || '-';
        const position = ['LF', 'CF', 'RF'].includes(rawPos) ? 'OF' : rawPos;
        return (
          <div className={`text-sm font-semibold ${dynastyTheme.classes.text.warning}`}>
            {position}
          </div>
        );
      }
    },

    // Player Name - with info icon for profile view
    {
      key: 'player_name',
      title: 'Player',
      width: 160,
      sortable: true,
      sortValue: (player) => player?.info?.full_name || '',
      allowOverflow: true,
      align: 'left',
      render: (player) => {
        const playerName = player?.info?.full_name || 'Unknown';

        return (
          <div className="text-left flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewProfile && onViewProfile(player);
              }}
              className={`p-0.5 rounded hover:bg-yellow-500/20 ${dynastyTheme.classes.text.neutralLight} hover:text-yellow-400 transition-colors flex-shrink-0`}
              title="View player profile"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onPlayerSelect && onPlayerSelect(player)}
              className={`${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.text.primary} ${dynastyTheme.classes.transitionFast} text-left cursor-pointer truncate`}
              style={{ textDecoration: 'none' }}
            >
              {playerName}
            </button>
            <InjuryBadge mlbId={player?.ids?.mlb} />
          </div>
        );
      }
    },

    // MLB Team - matches BoxScore Team column style
    {
      key: 'mlb_team',
      title: 'Team',
      width: 55,
      sortable: true,
      sortValue: (player) => player?.info?.mlb_team || '',
      render: (player) => {
        const mlbTeam = player?.info?.mlb_team || '-';
        return (
          <div className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight}`}>
            {mlbTeam}
          </div>
        );
      }
    },

    // Games Played (G) - matches BoxScore stat column style
    {
      key: 'games',
      title: 'G',
      width: 40,
      sortable: true,
      sortValue: (player) => {
        const stats = isHitter
          ? player?.stats?.[statsKey]?.hitting || {}
          : player?.stats?.[statsKey]?.pitching || {};
        return stats.games_played || stats.games || 0;
      },
      render: (player) => {
        const stats = isHitter
          ? player?.stats?.[statsKey]?.hitting || {}
          : player?.stats?.[statsKey]?.pitching || {};
        const value = stats.games_played || stats.games || 0;
        return <div className={dynastyTheme.classes.text.white}>{renderDefault(value)}</div>;
      }
    },

    // At Bats (AB) for hitters only - matches BoxScore stat column style
    ...(isHitter ? [{
      key: 'at_bats',
      title: 'AB',
      width: 45,
      sortable: true,
      sortValue: (player) => player?.stats?.[statsKey]?.hitting?.at_bats || 0,
      render: (player) => {
        const stats = player?.stats?.[statsKey]?.hitting || {};
        const value = stats.at_bats || 0;
        return <div className={dynastyTheme.classes.text.white}>{renderDefault(value)}</div>;
      }
    }] : []),

    // Hits (H) for hitters only - matches BoxScore stat column style
    ...(isHitter ? [{
      key: 'hits',
      title: 'H',
      width: 40,
      sortable: true,
      sortValue: (player) => player?.stats?.[statsKey]?.hitting?.hits || 0,
      render: (player) => {
        const stats = player?.stats?.[statsKey]?.hitting || {};
        const value = stats.hits || 0;
        return <div className={dynastyTheme.classes.text.white}>{renderDefault(value)}</div>;
      }
    }] : []),

    // Games Started (GS) for pitchers only - matches BoxScore stat column style
    ...(!isHitter ? [{
      key: 'games_started',
      title: 'GS',
      width: 40,
      sortable: true,
      sortValue: (player) => player?.stats?.[statsKey]?.pitching?.games_started || 0,
      render: (player) => {
        const stats = player?.stats?.[statsKey]?.pitching || {};
        const value = stats.games_started || 0;
        return <div className={dynastyTheme.classes.text.white}>{renderDefault(value)}</div>;
      }
    }] : []),

    // Innings Pitched (IP) for pitchers only - matches BoxScore stat column style
    ...(!isHitter ? [{
      key: 'innings_pitched',
      title: 'IP',
      width: 50,
      sortable: true,
      sortValue: (player) => player?.stats?.[statsKey]?.pitching?.innings_pitched || 0,
      render: (player) => {
        const stats = player?.stats?.[statsKey]?.pitching || {};
        const value = stats.innings_pitched || 0;
        return <div className={dynastyTheme.classes.text.white}>{renderFloat1(value)}</div>;
      }
    }] : [])
  ];

  // Get scoring categories or fall back to defaults
  const battingCategories = scoringCategories?.hitting || DEFAULT_BATTING_STATS;
  const pitchingCategories = scoringCategories?.pitching || DEFAULT_PITCHING_STATS;

  // Generate dynamic stat columns from league scoring categories
  const statColumns = isHitter
    ? createDynamicStatColumns(battingCategories, statsKey, 'hitting')
    : createDynamicStatColumns(pitchingCategories, statsKey, 'pitching');

  // Price column at the end - matches BoxScore highlighted column style
  const priceColumn = {
    key: 'baseline_price',
    title: '$',
    width: 45,
    sortable: true,
    sortValue: (player) => player?.financial?.baseline_price || 0,
    render: (player) => {
      const amount = player?.financial?.baseline_price;
      if (amount === null || amount === undefined) {
        return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
      }

      return (
        <div className={`font-bold ${dynastyTheme.classes.text.primary}`}>
          ${Math.round(amount)}
        </div>
      );
    }
  };

  // Actions column - add to queue button
  const actionsColumn = {
    key: 'actions',
    title: 'Queue',
    width: 60,
    sortable: false,
    render: (player) => {
      return (
        <button
          data-action="add-to-queue"
          data-player={JSON.stringify(player)}
          onClick={(e) => {
            e.stopPropagation();
            if (onAddToQueue) {
              onAddToQueue(player);
            }
          }}
          className={`p-1 rounded hover:bg-yellow-500/20 ${dynastyTheme.classes.text.primary} hover:text-yellow-300`}
          title="Add to queue"
        >
          <Plus className="w-4 h-4" />
        </button>
      );
    }
  };

  return [...baseColumns, ...statColumns, priceColumn, actionsColumn];
};

/**
 * Create dynamic stat columns based on league scoring categories
 * @param {Array<string>} categories - Stat abbreviations from league settings (e.g., ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'])
 * @param {string} statsKey - Stats object key: 'season' or 'season_YYYY'
 * @param {string} statType - 'hitting' or 'pitching'
 */
const createDynamicStatColumns = (categories, statsKey, statType) => {
  if (!categories || !Array.isArray(categories)) return [];

  return categories
    .map(abbrev => {
      const config = STAT_FIELD_MAP[abbrev];
      if (!config) return null;

      // Extract field name via the shared dedup helper (one implementation).
      const fieldName = statFieldSimple(config);

      // DD-STAT-CONSOLIDATION sweep: the static G/AB/H (hitting) and G/GS/IP
      // (pitching) columns already render these; drop the dynamic duplicate
      // (reached when categories fall back to DEFAULT_*_STATS, which contain them).
      const staticFields = statType === 'pitching' ? STATIC_PITCHING_FIELDS : STATIC_HITTING_FIELDS;
      if (staticFields.includes(fieldName)) return null;

      // No special highlighting - columns only highlight when sorted

      return {
        key: abbrev.toLowerCase(),
        title: config.label || abbrev,
        width: config.decimals > 0 ? 60 : 45,
        sortable: true,
        sortValue: (player) => {
          const stats = player?.stats?.[statsKey]?.[statType] || {};
          return stats[fieldName] || 0;
        },
        render: (player) => {
          const stats = player?.stats?.[statsKey]?.[statType] || {};
          const value = stats[fieldName];

          if (value === null || value === undefined) {
            return <div className={dynastyTheme.classes.text.neutralDark}>-</div>;
          }

          // Format based on decimals
          let formatted;
          if (config.decimals === 3 && config.prefix) {
            // AVG/OBP/SLG style: .249
            formatted = renderAvg(value);
          } else if (config.decimals === 3) {
            formatted = renderFloat3(value);
          } else if (config.decimals === 2) {
            formatted = renderFloat2(value);
          } else if (config.decimals === 1) {
            formatted = renderFloat1(value);
          } else {
            formatted = renderDefault(value);
          }

          return <div className={dynastyTheme.classes.text.white}>{formatted}</div>;
        }
      };
    })
    .filter(col => col !== null);
};

/**
 * Create hitter stat columns for a given stats key
 */
const createHitterStatColumns = (statsKey) => {
  const getStats = (player) => player?.stats?.[statsKey]?.hitting || {};

  return [
    {
      key: 'runs',
      title: 'R',
      width: 40,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderDefault(stats.runs || 0)}</span>;
      }
    },
    {
      key: 'home_runs',
      title: 'HR',
      width: 40,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderDefault(stats.home_runs || 0)}</span>;
      }
    },
    {
      key: 'rbi',
      title: 'RBI',
      width: 40,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderDefault(stats.rbi || 0)}</span>;
      }
    },
    {
      key: 'stolen_bases',
      title: 'SB',
      width: 40,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderDefault(stats.stolen_bases || 0)}</span>;
      }
    },
    {
      key: 'batting_avg',
      title: 'AVG',
      width: 50,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderAvg(stats.batting_avg)}</span>;
      }
    },
    {
      key: 'ops',
      title: 'OPS',
      width: 55,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderFloat3(stats.ops || 0)}</span>;
      }
    }
  ];
};

/**
 * Create pitcher stat columns for a given stats key
 */
const createPitcherStatColumns = (statsKey) => {
  const getStats = (player) => player?.stats?.[statsKey]?.pitching || {};

  return [
    {
      key: 'wins',
      title: 'W',
      width: 35,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderDefault(stats.wins || 0)}</span>;
      }
    },
    {
      key: 'saves',
      title: 'SV',
      width: 35,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderDefault(stats.saves || 0)}</span>;
      }
    },
    {
      key: 'strikeouts_pitched',
      title: 'K',
      width: 40,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderDefault(stats.strikeouts_pitched || 0)}</span>;
      }
    },
    {
      key: 'quality_starts',
      title: 'QS',
      width: 35,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderDefault(stats.quality_starts || 0)}</span>;
      }
    },
    {
      key: 'era',
      title: 'ERA',
      width: 50,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderFloat2(stats.era || 0)}</span>;
      }
    },
    {
      key: 'whip',
      title: 'WHIP',
      width: 55,
      sortable: true,
      render: (player) => {
        const stats = getStats(player);
        return <span className={dynastyTheme.classes.text.white}>{renderFloat2(stats.whip || 0)}</span>;
      }
    }
  ];
};
